import React, { useMemo, useState } from "react";
import { Label } from "../Label";
import { filterLocationOptions } from "../../helpers/popupCampaignTargeting";

interface Props {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Shown under the field, e.g. how many postal codes a selection covers. */
  hint?: string;
}

const MAX_VISIBLE = 50;

export const MultiSelectSearch: React.FC<Props> = ({
  label, options, selected, onChange, placeholder, hint,
}) => {
  const [query, setQuery] = useState("");
  // Shown on focus as well as on input: sensible for ~1,089 postal codes (a query narrows an
  // otherwise-unusable list), but wrong for a five-option list like Regions, which has no way to
  // be discovered without guessing a letter unless focusing alone reveals it.
  const [open, setOpen] = useState(false);

  // Every match, uncapped. MAX_VISIBLE limits only how many buttons are rendered; the true count
  // drives both the "showing N of M" line and "add all", and an admin who can't see the whole
  // list has to be told so — a silent alphabetical head of 605 cities reads as "Copenhagen is
  // missing from the data".
  const matches = useMemo(
    () => filterLocationOptions(options.filter((option) => !selected.includes(option)), query),
    [options, selected, query]
  );

  const visible = matches.slice(0, MAX_VISIBLE);
  const hiddenCount = matches.length - visible.length;
  const hasQuery = query.trim() !== "";

  return (
    <div>
      <Label>{label}</Label>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6d4c91] focus:border-transparent mt-1"
      />

      {open && (
        <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y">
          {matches.length === 0 && <p className="px-3 py-2 text-xs text-gray-500">No matches</p>}

          {/* DAWA splits a city into postal districts — Copenhagen is seven rows, Odense nine —
              so targeting one city means picking every district. This adds them in one go. */}
          {hasQuery && matches.length > 1 && (
            <button
              type="button"
              className="w-full text-left px-3 py-2 text-sm font-medium text-[#6d4c91] hover:bg-gray-50"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange([...selected, ...matches]);
                setQuery("");
              }}
            >
              Add all {matches.length}
            </button>
          )}

          {visible.map((option) => (
            <button
              key={option}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              // The input's blur (which would close this list) fires before a click's mouseup —
              // suppress it on mousedown so the list stays open and the click still lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange([...selected, option]);
                setQuery("");
              }}
            >
              {option}
            </button>
          ))}

          {hiddenCount > 0 && (
            <p className="px-3 py-2 text-xs text-gray-500">
              Showing {visible.length} of {matches.length}
              {hasQuery ? " — keep typing to narrow." : " — type to search."}
            </p>
          )}
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selected.map((value) => (
            <span key={value} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs">
              {value}
              <button
                type="button"
                aria-label={`Remove ${value}`}
                className="text-gray-500 hover:text-gray-800"
                onClick={() => onChange(selected.filter((v) => v !== value))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
};
